import { useEffect, useState } from 'react'
import { Dialog } from './Dialog'
import type { CardCopy } from '../lib/hubCopy'
import { detailsRefusal, locationLine, saveCardDetails, type CardDetails, type SignatureCard } from '../lib/signature'
import { initialsOf } from '../lib/initials'

/**
 * Edit your card: photo, mobile, office phone, office address. David, 6 Oct:
 * "options to add office phone number, address, etc? perhaps an edit button
 * and then a modal for editing?"
 *
 * ── WHAT IS HERE, AND WHAT IS NOT ───────────────────────────────────────────
 *
 * Everything on the card a person may choose. Name, title and email are NOT
 * here: they are HR's (BambooHR) and the company's (083's allow-list), and an
 * editable title on a card handed to a client is a claim nobody checked.
 *
 * ── ONE SAVE, ONE TRANSACTION ───────────────────────────────────────────────
 *
 * The form sends the WHOLE card to set_my_card_details (134), which is a
 * REPLACE: an emptied field is cleared, not ignored (rule 9). The mobile goes
 * through 083's set_my_work_phone inside it, so a refused office phone cannot
 * leave a new mobile half-saved.
 *
 * The PHOTO is the exception, and saves on its own: it already has its own
 * flow (pick, crop, upload — 132 and the crop dialog), and making a person
 * press Save after Use this photo would be a second confirmation of one act.
 *
 * ── A SIBLING OF THE CARD DIALOG ────────────────────────────────────────────
 *
 * For PhotoCropDialog's reason (read its header): inside the card Dialog's
 * React tree, its Tab would bubble into the card's focus trap. The crop
 * dialog, opened from here, is a third sibling, and useSurfaceLayer stacks
 * the three in the order they opened.
 */
export function CardEditDialog({
  open,
  card,
  photoUrl,
  photoReady,
  photoBusy,
  photoError,
  onChangePhoto,
  onRemovePhoto,
  onSaved,
  onClose,
  t,
}: {
  open: boolean
  card: SignatureCard | null
  photoUrl: string | null
  /** False until my_photo() has answered — no photo button that cannot upload (SHL-8). */
  photoReady: boolean
  photoBusy: boolean
  photoError: string | null
  onChangePhoto: () => void
  onRemovePhoto: () => void
  /** Called after a successful save, to reload the card. */
  onSaved: () => Promise<void>
  onClose: () => void
  t: CardCopy
}) {
  const [form, setForm] = useState<CardDetails>(() => detailsOf(card))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Every open starts from the card as it is now, not from an abandoned edit.
  useEffect(() => {
    if (open) {
      setForm(detailsOf(card))
      setError(null)
    }
    // `card` deliberately not a dependency: a reload mid-edit must not wipe
    // what the person is typing.
  }, [open])

  if (!card) return null

  const set = (k: keyof CardDetails) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await saveCardDetails(form)
      await onSaved()
      onClose()
    } catch (err) {
      const why = detailsRefusal((err as Error)?.message ?? '')
      setError(why === 'phone' ? t.phoneInvalid : why === 'address' ? t.addressIncomplete : t.detailsFailed)
    } finally {
      setSaving(false)
    }
  }

  const fallback = card ? locationLine(card) : ''

  return (
    <Dialog open={open} title={t.editTitle} onClose={onClose} className="editcardpanel">
      <form className="editcard" onSubmit={(e) => void onSubmit(e)} noValidate>
        <p className="editcard__hint">{t.detailsHint}</p>

        <fieldset className="editcard__group">
          <legend className="editcard__legend">{t.photoField}</legend>
          <div className="editcard__photo">
            <span className="editcard__face" aria-hidden="true">
              {photoUrl ? <img src={photoUrl} alt="" /> : initialsOf(card.displayName)}
            </span>
            {photoReady && (
              <div className="editcard__photobtns">
                <button type="button" className="btn btn--quiet btn--sm" disabled={photoBusy} onClick={onChangePhoto}>
                  {photoBusy ? t.photoSaving : photoUrl ? t.changePhoto : t.addPhoto}
                </button>
                {photoUrl && !photoBusy && (
                  <button type="button" className="btn btn--link" onClick={onRemovePhoto}>
                    {t.removePhoto}
                  </button>
                )}
              </div>
            )}
          </div>
          {photoError && <p className="profile__error" role="alert">{photoError}</p>}
        </fieldset>

        <div className="editcard__pair">
          <Field label={t.mobileField} value={form.mobile} onChange={set('mobile')} type="tel" autoComplete="tel" placeholder="+1 604 555 0142" />
          <Field label={t.officePhoneField} value={form.officePhone} onChange={set('officePhone')} type="tel" autoComplete="off" placeholder="+1 604 555 0100 x21" />
        </div>

        <fieldset className="editcard__group">
          <legend className="editcard__legend">{t.officeAddressHead}</legend>
          <p className="editcard__hint">{t.addressHint(fallback)}</p>
          <Field label={t.streetField} value={form.street} onChange={set('street')} autoComplete="street-address" />
          <div className="editcard__pair">
            <Field label={t.cityField} value={form.city} onChange={set('city')} autoComplete="address-level2" />
            <Field label={t.regionField} value={form.region} onChange={set('region')} autoComplete="address-level1" />
          </div>
          <div className="editcard__pair">
            <Field label={t.postcodeField} value={form.postcode} onChange={set('postcode')} autoComplete="postal-code" />
            <Field label={t.countryField} value={form.country} onChange={set('country')} autoComplete="country-name" />
          </div>
        </fieldset>

        {error && <p className="profile__error" role="alert">{error}</p>}

        <div className="editcard__btns">
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? t.saving : t.save}
          </button>
          <button type="button" className="btn btn--quiet" disabled={saving} onClick={onClose}>
            {t.cancel}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
  placeholder,
}: {
  label: string
  value: string | null
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  type?: 'text' | 'tel'
  autoComplete?: string
  placeholder?: string
}) {
  return (
    <label className="profile__field editcard__field">
      <span className="profile__fieldlabel">{label}</span>
      <input
        className="profile__input"
        type={type}
        inputMode={type === 'tel' ? 'tel' : undefined}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value ?? ''}
        onChange={onChange}
      />
    </label>
  )
}

/** The form's starting values: what the card says now. */
export function detailsOf(card: SignatureCard | null): CardDetails {
  const o = card?.office ?? null
  return {
    mobile: card?.workPhone ?? '',
    officePhone: card?.officePhone ?? '',
    street: o?.street ?? '',
    city: o?.city ?? '',
    region: o?.region ?? '',
    postcode: o?.postcode ?? '',
    country: o?.country ?? '',
  }
}
